import { useEffect, useRef, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import Notice from '../components/Notice';
import Spinner from '../components/Spinner';
import { useComparison } from '../hooks/ComparisonContext';
import { ApiError, api } from '../lib/api';
import { rememberReportToken } from '../lib/session';
import { tx } from '../i18n';

const POLL_INTERVAL_MS = 2000;
const MAX_ATTEMPTS = 20;

/**
 * Landing point after Stripe Checkout.
 *
 * <p>The browser reliably beats the webhook back, so this polls the cheap status endpoint rather
 * than hammering the report endpoint, and only redirects once the purchase is actually confirmed.
 */
export default function CheckoutSuccessPage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { refresh } = useComparison();
  const sessionId = searchParams.get('session_id');

  const [error, setError] = useState<string | null>(null);
  const [slow, setSlow] = useState(false);
  const attempts = useRef(0);

  useEffect(() => {
    if (!sessionId) {
      setError(tx('We could not match that payment to a purchase.', 'Bu ödeme bir satın alma ile eşleştirilemedi.',
    'Nie udało się powiązać tej płatności z zakupem.',
    'Não foi possível associar esse pagamento a uma compra.',
    'No se ha podido asociar ese pago a una compra.'));
      return undefined;
    }

    let cancelled = false;
    let timer: number | null = null;

    const poll = () => {
      api
        .checkoutStatus(sessionId)
        .then((status) => {
          if (cancelled) {
            return;
          }
          if (status.unlocked) {
            rememberReportToken(status.reportToken);
            refresh();
            navigate(status.analysisId ? `/report/${status.analysisId}` : '/compare', {
              replace: true,
            });
            return;
          }
          attempts.current += 1;
          if (attempts.current > MAX_ATTEMPTS) {
            setSlow(true);
            return;
          }
          timer = window.setTimeout(poll, POLL_INTERVAL_MS);
        })
        .catch((cause: unknown) => {
          if (cancelled) {
            return;
          }
          setError(
            cause instanceof ApiError
              ? cause.message
              : tx('We could not confirm that payment. Please contact us with your receipt.', 'Bu ödeme onaylanamadı. Lütfen makbuzunuzla bize ulaşın.',
    'Nie udało się potwierdzić tej płatności. Napisz do nas, podając potwierdzenie.',
    'Não foi possível confirmar esse pagamento. Contacte-nos com o seu recibo.',
    'No se ha podido confirmar ese pago. Escríbenos con tu recibo.'),
          );
        });
    };

    poll();
    return () => {
      cancelled = true;
      if (timer !== null) {
        window.clearTimeout(timer);
      }
    };
  }, [sessionId, navigate, refresh]);

  return (
    <div className="page page--narrow stack">
      {error ? (
        <Notice tone="error" title={tx('Payment received, report not found', 'Ödeme alındı, rapor bulunamadı',
    'Płatność otrzymana, raportu nie znaleziono',
    'Pagamento recebido, relatório não encontrado',
    'Pago recibido, informe no encontrado')}>
          {error}
        </Notice>
      ) : slow ? (
        <Notice tone="warning" title={tx('This is taking longer than usual', 'Bu normalden uzun sürüyor',
    'To trwa dłużej niż zwykle',
    'Isto está a demorar mais do que o habitual',
    'Esto está tardando más de lo normal')}>
          {tx(
            'Your payment has gone through but Stripe has not confirmed it to us yet. Your report will unlock on its own; try your comparison page in a minute.',
            'Ödemeniz alındı ama Stripe henüz bize onaylamadı. Raporunuz kendiliğinden açılacak; bir dakika sonra karşılaştırma sayfanıza bakın.',
    'Twoja płatność przeszła, ale Stripe jeszcze nam tego nie potwierdził. Raport odblokuje się sam; sprawdź stronę porównania za minutę.',
    'O seu pagamento foi efetuado mas a Stripe ainda não no-lo confirmou. O relatório desbloqueia sozinho; volte à página de comparação daqui a um minuto.',
    'Tu pago se ha realizado pero Stripe aún no nos lo ha confirmado. El informe se desbloqueará solo; vuelve a la página de comparación en un minuto.',
          )}
        </Notice>
      ) : (
        <div className="card loading-panel">
          <Spinner label={tx('Confirming your payment', 'Ödemeniz onaylanıyor',
    'Potwierdzanie płatności',
    'A confirmar o seu pagamento',
    'Confirmando tu pago')} />
          <p className="small muted">{tx('Taking you to your report.', 'Raporunuza yönlendiriliyorsunuz.',
    'Przenosimy cię do raportu.',
    'A levá-lo para o seu relatório.',
    'Te llevamos a tu informe.')}</p>
        </div>
      )}

      <Link to="/compare" className="button button--secondary" style={{ alignSelf: 'flex-start' }}>
        {tx('Go to my comparison', 'Karşılaştırmama git',
    'Przejdź do porównania',
    'Ir para a minha comparação',
    'Ir a mi comparación')}
      </Link>
    </div>
  );
}
